/**
 * Secret objectives (rules v0.6 §12, as amended by DESIGN v0.3 §6.16).
 * Evaluation lives in rules/objectives.ts.
 */
import type { Role } from '../types.ts';

export type ObjectiveGroup = 'individual' | 'team' | 'regional';

export interface ObjectiveCard {
  key: string;
  n: number;
  group: ObjectiveGroup;
  title: string;
  text: string;
  /** Only deal to these seats. */
  seats?: Role[];
}

export const OBJECTIVES: ObjectiveCard[] = [
  { key: 'THROUGHPUT', n: 1, group: 'individual', title: 'Throughput', text: 'Discharge or refer at least four patients from your list.' },
  { key: 'BOLD', n: 2, group: 'individual', title: 'Bold', text: 'Treat four patients on their provisional diagnosis without confirming, with no HIGH or CRITICAL outcome.' },
  { key: 'THOROUGH', n: 3, group: 'individual', title: 'Thorough', text: 'Confirm six patients before treating them.' },
  { key: 'MAYORS_COUSIN', n: 4, group: 'individual', title: 'The Mayor’s Cousin', text: 'Personally complete his plan.' },
  { key: 'TEACHING', n: 5, group: 'individual', title: 'Teaching at 0300', text: 'Use no actions three turns before the handover, and have no deaths on your list the turn after.' },
  { key: 'SELF_RELIANT', n: 6, group: 'individual', title: 'Self-reliant', text: 'The table never calls for help.' },
  { key: 'FRUGAL', n: 7, group: 'individual', title: 'Frugal', text: 'The table uses the Scanner no more than four times all shift.' },
  { key: 'CLEAN_SHEET', n: 8, group: 'individual', title: 'Clean sheet', text: 'No patient on your list deteriorates.' },
  { key: 'PROCEDURALIST', n: 9, group: 'individual', title: 'Proceduralist', text: 'Complete three Theatre steps yourself (Surgeon), or anaesthetise for three (Intensivist).', seats: ['SURG', 'ICU'] },
  { key: 'EARLY_FINISH', n: 10, group: 'individual', title: 'Early finish', text: 'Zero patients on your list at the start of the last turn.' },
  { key: 'AMBITION', n: 11, group: 'individual', title: 'Ambition', text: 'Be the Shift Lead at the handover.' },
  { key: 'CONSULTANT', n: 12, group: 'individual', title: 'Consultant', text: 'Perform four consults or helping actions on other lists.' },
  { key: 'TERRITORIAL', n: 13, group: 'individual', title: 'Territorial', text: 'Complete every plan on your list without a helping action from anyone.' },
  { key: 'CALL_EARLY', n: 16, group: 'team', title: 'Call early', text: 'The table calls for help before the middle of the shift.' },
  { key: 'TIMEOUT_EARLY', n: 18, group: 'team', title: 'Time-Out', text: 'The team uses its Time-Out before the last three turns.' },
  { key: 'NO_OVERFLOW_DELAYS', n: 19, group: 'team', title: 'Keep care moving', text: 'No patient’s discharge is delayed because this table is too busy.' },
  { key: 'AHEAD_OF_CURVE', n: 21, group: 'team', title: 'Ahead of the Curve', text: 'Book at least one escalation, with every request made before the table first has more patients than beds.' },
  { key: 'SOVEREIGN', n: 25, group: 'regional', title: 'Sovereign', text: 'The table never diverts.' },
  { key: 'GOOD_NEIGHBOUR', n: 26, group: 'regional', title: 'Good neighbour', text: 'The table accepts three or more patients from elsewhere.' },
  { key: 'FLOW', n: 27, group: 'regional', title: 'Flow', text: 'Every ICU step-down happens in the turn after its ICU steps complete.' },
  { key: 'HOME_EARLY', n: 28, group: 'regional', title: 'Home early', text: 'Early Discharge three patients with no bounce-back.' },
];

export function objectiveCard(key: string): ObjectiveCard {
  const o = OBJECTIVES.find((x) => x.key === key);
  if (!o) throw new Error(`Unknown objective '${key}'`);
  return o;
}

export const ROLE_INFO: Record<Role, { title: string; short: string; bed: string; bedPlural: string }> = {
  EM: { title: 'Emergency', short: 'Em', bed: 'Bay', bedPlural: 'Bays' },
  SURG: { title: 'Surgeon', short: 'Surg', bed: 'SW', bedPlural: 'Surgical ward' },
  PHYS: { title: 'Physician', short: 'Phys', bed: 'MW', bedPlural: 'Medical ward' },
  PSYCH: { title: 'Psychiatrist', short: 'Psych', bed: 'QW', bedPlural: 'Quiet Wing' },
  ICU: { title: 'Intensivist', short: 'ICU', bed: 'ICU', bedPlural: 'ICU' },
};
